import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import apiService from '../services/apiService';
import billingService from '../services/billingService';
import {
    SELF_SERVE_PLAN_OPTIONS, formatTierPoints, formatTierPrice, planLabel,
} from '../utils/planDisplay';
import LegalLinks from './LegalLinks';
import { useLanguage } from '../i18n';
import logger from '../utils/logger';

// Salary Selection — step 3 of onboarding (#374), turned into a PAYMENT gate by
// "Salary Gate Bypass" (#402).
//
//     1. Sign up → 2. Verify email (#335) → 3. THIS → 4. Main page
//
// Rendered INSTEAD of whatever the user asked for, from App.jsx's
// ProtectedRoute, exactly as the verification screen is. That placement is what
// makes "navigating to any other URL must redirect back" and "no features
// visible/interactable" structural rather than advisory: no protected route ever
// mounts for an account that still owes a choice, and the backend independently
// refuses every non-allowlisted request for one. Neither half relies on the
// other.
//
// 🛑 WHAT #402 REVERSED, AND WHAT THAT COSTS
// -----------------------------------------
// #374 opened the gate on the CHOICE, with a stated reason: if it only lifted
// once Stripe confirmed, a user who picks a tier and abandons the checkout page
// is stranded in a loop whose only exit is a site they just left. The PO reversed
// it ("a user session is only fully authorized after a successful payment webhook
// is received"), so that stranded state is now REACHABLE — and this component is
// where it has to be survivable. Hence THREE states rather than one list:
//
//   • `choose`   — the three PAID tiers. Free is gone: the gate clears on payment,
//                  so offering a tier that cannot be paid for would be offering a
//                  way past the paywall. (An admin can still grant Free.)
//   • `unpaid`   — a tier was picked (the backend's `awaiting_payment` + `plan`)
//                  but the checkout was never completed. "Your payment hasn't been
//                  completed" + a PRIMARY "Continue to payment" button that re-runs
//                  the record → checkout → redirect flow for the remembered plan.
//                  This is what a reopened browser lands on, and what a
//                  `?billing=cancelled` return lands on when a plan is known.
//   • `awaiting` — "Confirming your payment…". Entered ONLY from
//                  `?billing=success` or a 409 `already_subscribed` on checkout —
//                  i.e. only when there is evidence a payment was actually made.
//
// 🛑 PROD HOTFIX (2026-10-03): `awaiting` used to be seeded straight from the
// server's `awaiting_payment`. That flag only means "a tier was recorded and no
// webhook has settled it" — it cannot tell "I just paid" from "I opened Stripe
// and left". Every new account that abandoned the checkout came back to a
// spinner claiming a payment was being confirmed, with no visible way to pay
// (prod: four accounts, expired unpaid sessions, no PaymentIntent at all; one
// user polled that screen for 20+ minutes). Both `unpaid` and `awaiting` POLL the
// profile, so a webhook that does land (paid in another tab) still lifts the
// gate from either; only `awaiting` shows the two-minute "taking longer" note,
// because in `unpaid` "your payment is safe" would not be true.
//
// Sign out is offered in every state, unchanged: an account you can neither use
// nor leave is a trap (the #335 rule).
//
// THE `choose` STATE IS THE MARKETING SITE'S SALARY SECTION (Salary page #435).
// It mirrors pai-website-alien's `Pricing` component — eyebrow "Salaries", the
// heading "What your PAI earns.", the three salary cards (Entry / Associate /
// Partner, Associate highlighted as MOST POPULAR), each with its tagline, the
// VAT-exclusive monthly price, the Energy box (Zap icon + points / mo), the four
// perks and "Hire your own PAI", then the roll-over note — copy verbatim in en
// and es, restyled in the app's dark glass. Deliberately NOT mirrored: the
// monthly/yearly toggle (Stripe sells monthly only, so a yearly price here would
// be a price nobody can buy) and the country VAT calculator (Stripe Tax computes
// the real figure on the checkout page; each card says "+ VAT for your country",
// which is exactly what the site shows before a country is chosen).
//
// 🛑 NO NUMBER ON THIS PAGE IS TYPED IN THE CLIENT (Salary&points: Alignment
// #436). Points and prices come from `salarySelection.tiers` — served by
// /auth/profile from BE plans.PLAN_DEPOSIT_PTS / PLAN_PRICES_EUR, the very
// tables the deposit is paid from and Stripe is priced in. So what the user is
// shown is, by construction, what lands in their tank. An older payload with no
// catalog still renders the three cards (from the id list) without figures,
// rather than inventing them.
//
// The pick button keeps the page's contract: `salary-selection-option` +
// `data-plan` (the INTERNAL id — base / executive / enterprise) + `data-busy`.
// It sits on the card's CTA rather than the card itself because the CTA is the
// one element that acts; the card carries `salary-tier-card` + `data-tier`.

/** How often the awaiting screen re-reads the profile, ms. Matches the shell's
 *  own billing-return poll — the webhook normally lands within a couple of
 *  seconds, and this is the same question asked from the other side of the gate. */
const POLL_MS = 3000;
/** After this long we stop implying it is imminent and say so. Polling CONTINUES
 *  — the payment is real and the webhook may still be in flight; what changes is
 *  that the user is told rather than left watching a spinner. */
const SLOW_AFTER_MS = 120000;

/** Set when the user returns on `?billing=success`. The server's
 *  `awaiting_payment` cannot tell a payer from someone who abandoned the
 *  checkout, so a RELOAD shortly after a real Stripe success would otherwise
 *  land on "payment not completed" while the webhook is still in flight. Within
 *  BILLING_SUCCESS_TTL_MS of the marker a reload resumes `awaiting` instead.
 *  Cleared when the gate lifts, on "Choose a different plan", and whenever a new
 *  checkout is started. Best-effort: storage failures just mean no marker. */
export const BILLING_SUCCESS_KEY = 'omnis:billing-success-at';
export const BILLING_SUCCESS_TTL_MS = 10 * 60 * 1000;

const markBillingSuccess = () => {
    try { window.localStorage.setItem(BILLING_SUCCESS_KEY, String(Date.now())); } catch { /* ignore */ }
};
const clearBillingSuccess = () => {
    try { window.localStorage.removeItem(BILLING_SUCCESS_KEY); } catch { /* ignore */ }
};
const recentBillingSuccess = () => {
    try {
        const at = Number(window.localStorage.getItem(BILLING_SUCCESS_KEY));
        if (!Number.isFinite(at) || at <= 0) return false;
        const age = Date.now() - at;
        // A future timestamp (clock skew) is treated as fresh, not as forever.
        return age < BILLING_SUCCESS_TTL_MS && age > -BILLING_SUCCESS_TTL_MS;
    } catch {
        return false;
    }
};

/** The website's per-salary copy, keyed by the INTERNAL tier id. Literal t()
 *  calls (not built keys) so the i18n contract test can check every one. */
const tierCopy = (t) => ({
    base: {
        tagline: t('salaryTierTaglineBase', 'Your day-to-day, off your mind.'),
        perks: [
            t('salaryPerkEveryCapability', 'Every capability, nothing locked'),
            t('salaryPerkRollOver', 'Unused points roll over'),
            t('salaryPerkTelegramWeb', 'Telegram and the web'),
            t('salaryPerkStandardSupport', 'Standard support'),
        ],
    },
    executive: {
        tagline: t('salaryTierTaglineExecutive', 'Your life and your work, same brain.'),
        featured: true,
        perks: [
            t('salaryPerkEveryCapability', 'Every capability, nothing locked'),
            t('salaryPerkRollOver', 'Unused points roll over'),
            t('salaryPerkFasterSupport', 'Reduced support response time'),
            t('salaryPerkEarlyAccess', 'Early access to new features'),
        ],
    },
    enterprise: {
        tagline: t('salaryTierTaglineEnterprise', 'For people whose day is other people.'),
        perks: [
            t('salaryPerkEveryCapability', 'Every capability, nothing locked'),
            t('salaryPerkRollOver', 'Unused points roll over'),
            t('salaryPerkPrioritySupport', 'Priority support'),
            t('salaryPerkEarlyAccess', 'Early access to new features'),
        ],
    },
});

/** The cards to render: the server's catalog when present (its order, its
 *  figures), else the self-serve id list with NO figures — never invented ones. */
export const salaryCards = (tiers) => {
    const served = Array.isArray(tiers)
        ? tiers.filter((tier) => tier && tier.id && tier.id !== 'free')
        : [];
    if (served.length) return served;
    return SELF_SERVE_PLAN_OPTIONS.map(({ value }) => ({ id: value }));
};

const SalaryTierCard = ({ tier, copy, busyPlan, onChoose }) => {
    const { t, language } = useLanguage();
    const { id } = tier;
    const busy = busyPlan === id;
    const featured = !!copy?.featured;
    const price = formatTierPrice(tier.price_eur, language);
    const points = formatTierPoints(tier.deposit_pts, language);
    return (
        <li
            data-testid="salary-tier-card"
            data-tier={id}
            data-featured={featured ? 'true' : 'false'}
            className={`flex flex-col gap-5 min-w-0 p-7 rounded-[22px] ${
                featured
                    ? 'border-[1.5px] border-white/80 bg-[rgba(30,37,48,0.94)] shadow-[0_0_40px_rgba(255,255,255,0.09)]'
                    : 'border border-white/10 bg-[rgba(14,18,25,0.82)]'
            } ${busyPlan && !busy ? 'opacity-50' : ''}`}
        >
            <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 data-testid="salary-tier-name" className="text-[17px] font-bold leading-none text-white">
                        {t(`plan_${id}`, planLabel(id))}
                    </h3>
                    {featured && (
                        <span
                            data-testid="salary-tier-popular"
                            className="px-2.5 py-[5px] rounded-[7px] bg-white text-pai-void text-[10.5px] font-bold leading-none tracking-[0.07em]"
                        >
                            {t('salaryMostPopular', 'MOST POPULAR')}
                        </span>
                    )}
                </div>
                {copy?.tagline && (
                    <p className="mt-[7px] text-[13.5px] leading-[1.5] text-white/[0.86] break-words">
                        {copy.tagline}
                    </p>
                )}
            </div>

            {price && (
                <div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span data-testid="salary-tier-price"
                              className="text-[40px] font-extrabold leading-none tracking-[-0.04em] text-white">
                            {price}
                        </span>
                        <span className="text-[14px] font-medium leading-none text-white/[0.89]">
                            {t('salaryPerMonth', '/ month')}
                        </span>
                    </div>
                    <div className="mt-3 text-[14px] font-semibold leading-[1.5] text-white/[0.95]">
                        {t('salaryVatForCountry', '+ VAT for your country')}
                    </div>
                </div>
            )}

            <div className={`rounded-[14px] px-[18px] py-4 ${
                featured
                    ? 'border border-white/[0.22] bg-[rgba(34,42,54,0.95)]'
                    : 'border border-white/10 bg-[rgba(20,25,33,0.86)]'
            }`}>
                <div className="flex items-center gap-[9px]">
                    <Zap aria-hidden="true" size={16} className="flex-shrink-0 text-white/70" />
                    <span className="text-[12px] font-bold leading-none tracking-[0.1em] uppercase text-white/[0.92]">
                        {t('salaryEnergy', 'Energy')}
                    </span>
                </div>
                <div className="mt-[11px] text-[26px] font-extrabold leading-none tracking-[-0.03em] text-white tnum">
                    <span data-testid="salary-tier-points" data-points={tier.deposit_pts ?? ''}>
                        {points ?? '—'}
                    </span>{' '}
                    <span className="text-[13px] font-semibold tracking-normal text-white/[0.89]">
                        {t('salaryPointsPerMonth', 'points / mo')}
                    </span>
                </div>
            </div>

            <ul className="flex flex-col gap-2.5">
                {(copy?.perks || []).map((perk) => (
                    <li key={perk} className="flex gap-2.5 text-[14.5px] leading-[1.5] text-white/[0.91] min-w-0">
                        <span aria-hidden="true" className="text-white/30">•</span>
                        <span className="min-w-0 break-words">{perk}</span>
                    </li>
                ))}
            </ul>

            <button
                type="button"
                data-testid="salary-selection-option"
                data-plan={id}
                data-busy={busy ? 'true' : 'false'}
                disabled={!!busyPlan}
                onClick={() => onChoose(id)}
                className={`mt-auto h-11 w-full flex items-center justify-center gap-2 rounded-[11px] text-[14.5px] min-w-0 transition-colors disabled:cursor-default ${
                    featured
                        ? 'bg-white text-pai-void font-bold hover:bg-white/90'
                        : 'border border-white/[0.16] bg-[rgba(20,25,33,0.86)] text-white font-semibold hover:border-white/40 hover:bg-white/[0.08]'
                }`}
            >
                {busy && <Loader2 aria-hidden="true" size={14} className="flex-shrink-0 animate-spin" />}
                <span className="truncate min-w-0">
                    {busy
                        ? t('salaryRedirecting', 'Redirecting to secure checkout…')
                        : t('salaryHireCta', 'Hire your own PAI')}
                </span>
            </button>
        </li>
    );
};

const SalaryChooser = ({ tiers, busyPlan, cancelled, error, onChoose, onLogout }) => {
    const { t } = useLanguage();
    const copy = tierCopy(t);
    return (
        <section data-testid="salary-selection-choose" className="min-w-0 text-white">
            <div className="text-center max-w-[640px] mx-auto">
                <div className="text-[12px] font-bold leading-none tracking-[0.14em] uppercase text-pai-blue">
                    {t('salaryPageEyebrow', 'Salaries')}
                </div>
                <h2
                    data-testid="salary-selection-title"
                    className="mt-[18px] text-[clamp(30px,4vw,46px)] font-extrabold leading-[1.08] tracking-[-0.035em] text-white [text-wrap:balance]"
                >
                    {t('salaryPageTitle', 'What your PAI earns.')}
                </h2>
                <p className="mt-4 text-[15.5px] leading-[1.6] text-white/[0.84]">
                    {t('salaryPageIntro',
                        'Every salary includes every capability, so nothing is locked. Like any salary, what changes is how much work you can ask of it each month.')}
                </p>
            </div>

            {cancelled && (
                <div
                    data-testid="salary-selection-cancelled"
                    role="status"
                    className="mt-6 max-w-[640px] mx-auto p-4 text-sm text-pai-meta border border-pai bg-white/[0.04] rounded-ctl break-words"
                >
                    {t('salarySelectCancelled',
                        'Checkout cancelled — nothing was charged. Pick a salary to finish setting up your account.')}
                </div>
            )}

            {error && (
                <div
                    data-testid="salary-selection-error"
                    role="alert"
                    className="mt-6 max-w-[640px] mx-auto p-4 text-sm text-pai-red border border-[rgba(240,68,56,0.4)] bg-[rgba(240,68,56,0.08)] rounded-ctl"
                >
                    {error}
                </div>
            )}

            <ul
                data-testid="salary-tier-grid"
                className="mt-[clamp(28px,4vw,44px)] grid gap-3.5 items-stretch"
                style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(276px,100%),1fr))' }}
            >
                {salaryCards(tiers).map((tier) => (
                    <SalaryTierCard
                        key={tier.id}
                        tier={tier}
                        copy={copy[tier.id]}
                        busyPlan={busyPlan}
                        onChoose={onChoose}
                    />
                ))}
            </ul>

            <p className="mt-5 max-w-[860px] mx-auto text-center text-[13.5px] leading-[1.5] text-white/[0.89]">
                {t('salaryRollOverNote',
                    'Unused points roll over to next month, so you never lose them. A point is one unit of thinking. Filing a receipt costs a handful, a deep research run costs a few thousand. Let your PAI go any time, no notice period, and take all your data with you.')}
            </p>
            <p data-testid="salary-selection-note"
               className="mt-3 max-w-[640px] mx-auto text-center text-[11px] text-pai-meta break-words">
                {t('salarySelectNote',
                    'Every salary continues to our secure Stripe checkout, which shows the exact amount. Prices exclude VAT — tax is added at checkout.')}
            </p>

            <div className="mt-6 flex justify-center text-sm">
                <button
                    type="button"
                    data-testid="salary-selection-logout"
                    onClick={onLogout}
                    className="text-pai-meta hover:text-white transition-colors"
                >
                    {t('logout', 'Logout')}
                </button>
            </div>
        </section>
    );
};

const SalarySelectionPage = ({ salarySelection, onChosen, onLogout }) => {
    const { t } = useLanguage();
    const [busyPlan, setBusyPlan] = useState(null);
    const [error, setError] = useState('');
    const chosenPlan = salarySelection?.plan || null;
    // Seeded from the SERVER's view, so a reopened browser resumes in the right
    // state with no client-side memory at all. A recorded-but-unsettled tier is
    // `unpaid`, NEVER `awaiting`: the server cannot know a payment was made, so
    // claiming one is being confirmed would be a guess (the 2026-10-03 hotfix).
    // A recorded flag with no plan has nothing to pay for — show the tiers.
    // The one exception: a reload within BILLING_SUCCESS_TTL_MS of a real
    // `?billing=success` return resumes `awaiting` (webhook still in flight).
    const [mode, setMode] = useState(() => {
        if (!(salarySelection?.awaiting_payment && chosenPlan)) return 'choose';
        return recentBillingSuccess() ? 'awaiting' : 'unpaid';
    });
    const awaiting = mode === 'awaiting';
    const [cancelled, setCancelled] = useState(false);
    const [slow, setSlow] = useState(false);

    // The Stripe return. WorkspaceShell consumes `?billing` too, but it never
    // mounts while the gate is closed, so there is no contest — and stripping the
    // param here means a reload does not re-enter `awaiting` after the user has
    // gone back to the tiers.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const outcome = params.get('billing');
        if (!outcome) return;
        params.delete('billing');
        const rest = params.toString();
        window.history.replaceState(
            {}, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash,
        );
        if (outcome === 'success') {
            markBillingSuccess();
            setMode('awaiting');
        } else if (outcome === 'cancelled') {
            // Same fact as an abandoned checkout: if the tier is known, offer to
            // pay for it again; otherwise back to the list. Either way, say so.
            setCancelled(true);
            setMode(chosenPlan ? 'unpaid' : 'choose');
        }
        // Mount-only by design: the param is consumed once and stripped above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The poll. A recursive setTimeout rather than setInterval so a slow profile
    // read can never stack requests, and a `cancelled` flag rather than clearing a
    // handle so an in-flight response cannot act after unmount.
    const onChosenRef = useRef(onChosen);
    onChosenRef.current = onChosen;
    // Runs in BOTH `awaiting` and `unpaid`: an unpaid user may have paid in
    // another tab, and the gate must lift here too when that webhook lands.
    useEffect(() => {
        if (mode === 'choose') return undefined;
        const showSlow = mode === 'awaiting';
        let stopped = false;
        let timer = null;
        const startedAt = Date.now();

        const tick = async () => {
            if (stopped) return;
            try {
                const profile = await apiService.getUserProfile();
                if (stopped) return;
                // `required === false` is the ONLY exit: it is exactly what
                // App.jsx gates on, so the screen and the router can never
                // disagree about whether onboarding is finished.
                if (profile?.salary_selection?.required === false) {
                    clearBillingSuccess();
                    await onChosenRef.current?.();
                    return;
                }
            } catch (err) {
                // A failed read is a reason to try again, never a reason to let
                // the user through — the whole of #402 is that "unknown" is not
                // "nothing outstanding".
                logger.warn('salary payment poll failed', err);
            }
            if (stopped) return;
            if (showSlow && Date.now() - startedAt > SLOW_AFTER_MS) setSlow(true);
            timer = setTimeout(tick, POLL_MS);
        };

        timer = setTimeout(tick, POLL_MS);
        return () => { stopped = true; if (timer) clearTimeout(timer); };
    }, [mode]);

    const choose = async (plan) => {
        if (busyPlan) return;
        setError('');
        setCancelled(false);
        setBusyPlan(plan);
        // A new checkout supersedes any earlier success marker ("Continue to
        // payment" and a tier pick both come through here).
        clearBillingSuccess();
        try {
            // Records the INTENT; the gate stays shut until Stripe confirms.
            // Deliberately before the redirect, so a user who never comes back
            // still returns to "confirming your payment" (with a way out) rather
            // than to a screen that has forgotten what they picked.
            const result = await apiService.post('/auth/salary-selection', { plan });
            if (result?.checkout_required) {
                const { url } = await billingService.createCheckout(plan);
                window.location.assign(url);
                return;
            }
            // No checkout needed (an admin settled it, or a future free/trial
            // tier) — re-read and let the gate lift.
            clearBillingSuccess();
            await onChosen?.();
        } catch (err) {
            const type = err?.body?.error?.type;
            // The gate is already open (settled in another tab, grandfathered, or
            // the flag went off) — re-read and let it lift rather than showing an
            // error for something that is not one.
            if (type === 'not_required') {
                clearBillingSuccess();
                await onChosen?.();
                return;
            }
            if (type === 'already_subscribed') {
                // A live subscription whose webhook has not landed yet. Waiting is
                // the correct answer; a second checkout would double-bill.
                setMode('awaiting');
                setBusyPlan(null);
                return;
            }
            logger.warn('salary selection failed', err);
            setError(t('salarySelectError', "We couldn't save that choice — please try again."));
            setBusyPlan(null);
        }
    };

    const backToTiers = useCallback(() => {
        clearBillingSuccess();
        setMode('choose');
        setSlow(false);
        setError('');
    }, []);

    return (
        <div
            data-testid="salary-selection-screen"
            className="relative flex justify-center min-h-dvh overflow-y-auto bg-pai-void text-white"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/wallpaper.jpg')" }}
            />
            <div aria-hidden="true" className="absolute inset-0 bg-[rgba(5,7,10,0.64)]" />
            {/* `my-auto` rather than `items-center` on the parent: the salary
                grid is taller than a phone screen, and a centred flex child that
                overflows its container is clipped at the TOP, out of scroll reach. */}
            <div className={`relative w-full my-auto py-10 ${mode === 'choose' ? 'max-w-[1180px] px-4 sm:px-6' : 'max-w-md px-6'}`}>
                {mode === 'choose' ? (
                    <SalaryChooser
                        tiers={salarySelection?.tiers}
                        busyPlan={busyPlan}
                        cancelled={cancelled}
                        error={error}
                        onChoose={choose}
                        onLogout={onLogout}
                    />
                ) : (
                <div className="w-full p-8 space-y-6 rounded-modal border border-white/[0.14] bg-white/[0.03] backdrop-blur-tile">
                    {mode === 'unpaid' ? (
                        <div data-testid="salary-unpaid" className="space-y-6">
                            <div className="text-center space-y-3">
                                <h2 className="text-[24px] font-extrabold tracking-[-0.025em] text-white">
                                    {t('salaryUnpaidTitle', "Your payment hasn't been completed")}
                                </h2>
                                <p className="text-sm text-pai-meta break-words">
                                    {t('salaryUnpaidBody',
                                        'The payment for this salary was not finished. Continue to payment to open your account.')}
                                </p>
                                <p data-testid="salary-unpaid-plan"
                                   className="text-sm font-semibold text-white break-words">
                                    {t('salaryUnpaidPlan', 'Selected salary: {plan}')
                                        .replace('{plan}', t(`plan_${chosenPlan}`, planLabel(chosenPlan)))}
                                </p>
                            </div>

                            {cancelled && (
                                <div
                                    data-testid="salary-selection-cancelled"
                                    role="status"
                                    className="p-4 text-sm text-pai-meta border border-pai bg-white/[0.04] rounded-ctl break-words"
                                >
                                    {t('salarySelectCancelled',
                                        'Checkout cancelled — nothing was charged. Pick a salary to finish setting up your account.')}
                                </div>
                            )}

                            {error && (
                                <div
                                    data-testid="salary-selection-error"
                                    role="alert"
                                    className="p-4 text-sm text-pai-red border border-[rgba(240,68,56,0.4)] bg-[rgba(240,68,56,0.08)] rounded-ctl"
                                >
                                    {error}
                                </div>
                            )}

                            {/* The PRIMARY action, as prominent as a tier button:
                                re-runs record → checkout → redirect for the
                                remembered plan (choose() handles not_required and
                                already_subscribed exactly as the tier list does). */}
                            <button
                                type="button"
                                data-testid="salary-unpaid-pay"
                                data-plan={chosenPlan}
                                data-busy={busyPlan ? 'true' : 'false'}
                                disabled={!!busyPlan}
                                onClick={() => choose(chosenPlan)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-ctl border border-white/40 bg-white/[0.10] hover:bg-white/[0.16] text-sm font-semibold text-white min-w-0 transition-colors disabled:opacity-60"
                            >
                                {busyPlan && (
                                    <Loader2 aria-hidden="true" size={14} className="flex-shrink-0 animate-spin" />
                                )}
                                <span className="truncate min-w-0">
                                    {busyPlan
                                        ? t('salaryRedirecting', 'Redirecting to secure checkout…')
                                        : t('salaryUnpaidPay', 'Continue to payment')}
                                </span>
                            </button>

                            <div className="flex flex-col items-center gap-3 text-sm">
                                <button
                                    type="button"
                                    data-testid="salary-unpaid-change"
                                    onClick={backToTiers}
                                    disabled={!!busyPlan}
                                    className="text-pai-meta hover:text-white transition-colors"
                                >
                                    {t('salaryAwaitingChange', 'Choose a different plan')}
                                </button>
                                <button
                                    type="button"
                                    data-testid="salary-selection-logout"
                                    onClick={onLogout}
                                    className="text-pai-meta hover:text-white transition-colors"
                                >
                                    {t('logout', 'Logout')}
                                </button>
                            </div>
                        </div>
                    ) : awaiting ? (
                        <div data-testid="salary-awaiting-payment" className="space-y-6">
                            <div className="text-center space-y-3">
                                <Loader2
                                    aria-hidden="true"
                                    size={28}
                                    className="mx-auto animate-spin text-white/70"
                                />
                                <h2 className="text-[24px] font-extrabold tracking-[-0.025em] text-white">
                                    {t('salaryAwaitingTitle', 'Confirming your payment…')}
                                </h2>
                                <p className="text-sm text-pai-meta break-words">
                                    {t('salaryAwaitingBody',
                                        'Your account opens as soon as the payment is confirmed. This usually takes a few seconds — you can leave this page open.')}
                                </p>
                                {chosenPlan && (
                                    <p data-testid="salary-awaiting-plan"
                                       className="text-[11px] text-pai-faint">
                                        {t(`plan_${chosenPlan}`, planLabel(chosenPlan))}
                                    </p>
                                )}
                            </div>

                            {slow && (
                                <div
                                    data-testid="salary-awaiting-timeout"
                                    role="status"
                                    className="p-4 text-[12px] text-pai-meta border border-pai bg-white/[0.04] rounded-ctl break-words"
                                >
                                    {t('salaryAwaitingSlow',
                                        'This is taking longer than expected. Your payment is safe — refresh this page, or contact support if it does not clear shortly.')}
                                </div>
                            )}

                            <div className="flex flex-col items-center gap-3 text-sm">
                                <button
                                    type="button"
                                    data-testid="salary-awaiting-change"
                                    onClick={backToTiers}
                                    className="text-pai-meta hover:text-white transition-colors"
                                >
                                    {t('salaryAwaitingChange', 'Choose a different plan')}
                                </button>
                                {/* Always offered — an account you can neither use
                                    nor leave is a trap. */}
                                <button
                                    type="button"
                                    data-testid="salary-selection-logout"
                                    onClick={onLogout}
                                    className="text-pai-meta hover:text-white transition-colors"
                                >
                                    {t('logout', 'Logout')}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
                )}
                <LegalLinks className="mt-6" />
            </div>
        </div>
    );
};

export default SalarySelectionPage;
